import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PinInput } from "@/components/PinInput";
import { Logo } from "@/components/Logo";
import { storage } from "@/lib/storage";
import { CheckCircle2 } from "lucide-react";

export default function PinSetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: set, 2: confirm
  const [firstPin, setFirstPin] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!storage.getUser()) navigate("/", { replace: true });
  }, [navigate]);

  const handleSet = (pin) => {
    setFirstPin(pin);
    setStep(2);
  };

  const handleConfirm = (pin) => {
    if (pin !== firstPin) {
      setError("PINs don't match. Try again.");
      setStep(1);
      setFirstPin("");
      return;
    }
    storage.setPin(pin);
    storage.touchUnlock();
    if (!storage.getProfile()) {
      const rand = Array.from(crypto.getRandomValues(new Uint8Array(6)), (b) => b.toString(16).padStart(2, "0")).join("");
      storage.setProfile({ name: "Merchant", id: `M${rand.toUpperCase()}` });
    }
    storage.setOnboarded();
    const role = sessionStorage.getItem("opg_pending_role") || "merchant";
    sessionStorage.removeItem("opg_pending_role");
    navigate(role === "merchant" ? "/merchant" : "/customer");
  };

  return (
    <div className="page-shell flex flex-col items-center" data-testid="pin-setup-page">
      <div className="w-full flex items-center justify-center mb-8 fade-slide-up">
        <Logo size={54} />
      </div>
      <div className="text-center mb-8 fade-slide-up" style={{ animationDelay: "0.08s" }}>
        <h1 className="text-3xl font-bold mb-2 gold-text" data-testid="pin-setup-title">
          {step === 1 ? "Create your PIN" : "Confirm PIN"}
        </h1>
        <p className="text-sm text-[#F7E6B4]/60 max-w-sm">
          {step === 1
            ? "Choose a 4-digit PIN to secure every offline transaction."
            : "Enter the same 4-digit PIN again to confirm."}
        </p>
      </div>

      <div className="fade-slide-up" style={{ animationDelay: "0.16s" }}>
        {step === 1 ? (
          <PinInput key="step1" onComplete={handleSet} label="Set a new 4-digit PIN" error={error} />
        ) : (
          <PinInput key="step2" onComplete={handleConfirm} label="Re-enter your PIN" />
        )}
      </div>

      <div className="mt-10 flex items-center gap-2 text-xs text-[#F7E6B4]/50 fade-slide-up">
        <CheckCircle2 size={14} className="text-[#E6B455]" />
        Stored on-device only · Never leaves your phone
      </div>
    </div>
  );
}
